import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { IApp } from "@/types";
import { apiURL, makeAlert, sendRequest } from "@/utils";
import { URLs } from "@/constants";
import _ from "lodash";
import { useAuthStore } from "./auth";
import Auth from "@/utils/auth";

export const useCustomerStore = defineStore("customer", () => {
  const state = ref<{
    customersListStatus: IApp.ILoadingStatus;
  }>({
    customersListStatus: IApp.ILoadingStatus.Idle
  });

  const isCustomersListLoading = computed(
    () => state.value.customersListStatus === IApp.ILoadingStatus.Loading
  );

  const customers = ref<IApp.ICustomer[]>([]);

  const fetchCustomers = async (userId: string) => {
    const authStore = useAuthStore();
    if (!authStore.hasPermission(Auth.Permission.CUSTOMER_LIST)) {
      makeAlert("You don't have permission to see the customer list", "error");
      state.value.customersListStatus = IApp.ILoadingStatus.Error;
      return;
    }
    state.value.customersListStatus = IApp.ILoadingStatus.Loading;
    const response = await sendRequest<{ customers: IApp.ICustomer[] }>({
      url: apiURL(`${URLs.API_CUSTOMER_LIST(userId)}`),
      method: "GET"
    });
    if (response.status) {
      customers.value = _(response.data.customers).sortBy("createdAt").reverse().value();
      state.value.customersListStatus = IApp.ILoadingStatus.Success;
    } else {
      makeAlert(response.message, "error");
      state.value.customersListStatus = IApp.ILoadingStatus.Error;
    }
  };

  const createCustomer = async (userId: string, customer: IApp.ICreateData<IApp.ICustomer>) => {
    return await sendRequest<{ customer: IApp.ICustomer }>({
      url: apiURL(URLs.API_CUSTOMER_CREATE(userId)),
      method: "POST",
      data: customer
    });
  };

  const patchCustomer = async (userId: string, customer: Pick<IApp.ICustomer, "name" | "id">) => {
    return await sendRequest<{ customer: IApp.ICustomer }>({
      url: apiURL(URLs.API_CUSTOMER_PATCH(userId, customer.id!)),
      method: "PATCH",
      data: {
        name: customer.name
      }
    });
  };

  const deleteCustomer = async (userId: string, customerId: string) => {
    const response = await sendRequest<{ customer?: IApp.ICustomer }>({
      url: apiURL(URLs.API_CUSTOMER_DELETE(userId, customerId)),
      method: "DELETE"
    });
    if (response.status) {
      const index = _.findIndex(customers.value, { id: customerId });
      if (index !== -1) {
        if (response.data?.customer) {
          customers.value[index] = { ...customers.value[index], ...response.data.customer };
        } else {
          customers.value[index] = {
            ...customers.value[index],
            deletedAt: new Date().toISOString()
          };
        }
      }
    }
    return response;
  };

  const getCustomer = (id: string): IApp.ICustomer | undefined => {
    return _.find(customers.value, { id });
  };

  return {
    customers,
    isCustomersListLoading,
    fetchCustomers,
    createCustomer,
    patchCustomer,
    deleteCustomer,
    getCustomer
  };
});
